import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  CertificationV2TypeEnum,
  MissingBenefitPeriod,
} from "certification/models/cti";
import moment from "moment";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import { catchError, finalize, shareReplay } from "rxjs/operators";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import { ConfigurationService } from "../../providers/configuration/configuration.service";
import { CertificationRerportType as CertificationReportType } from "./../models/cti";

export class CertificationPeriod {
  certificationFromDate: string;
  certificationToDate: string;
}

export class CertificationF2FNarrative {
  attestationText: string;
  signatory: string;
  signatoryDate: string;
}

export class WrittenCTIRequiredV2Response {
  required: boolean;
  status: string;
}

@Injectable()
export class CertificationService {
  protected certificationUrl;
  resourceManagementUrl: string;
  reportsUrl: string;
  private benefitPeriodCache = new Map<string, Observable<any>>();
  idgDataUrl: string;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.resourceManagementUrl = config.getConfig().resourceManagementUrl;
    this.reportsUrl = config.getConfig().reportsUrl;

    if (this.config.getConfig().isLocal) {
      this.resourceManagementUrl = config.getConfig().localEndpoints
        .resourceManagementUrl
        ? config.getConfig().localEndpoints.resourceManagementUrl
        : config.getConfig().apiUrl;
    } else {
      this.resourceManagementUrl = config.getConfig().apiUrl;
    }

    this.certificationUrl =
      config.getConfig().isLocal &&
      config.getConfig().localEndpoints.certificationUrl
        ? config.getConfig().localEndpoints.certificationUrl
        : config.getConfig().apiUrl + "/PatientCertification";
    this.idgDataUrl = config.resolveIdgUrl();
  }

  public getWrittenRequiredStatusV2(patientId: string, dareStatus: string) {
    let writtenStatusUrl = `${this.certificationUrl}/v2/certifications/written/verify/${patientId}?dareStatus=${dareStatus}`;
    return new Observable<WrittenCTIRequiredV2Response>((observer) => {
      this.http.get(writtenStatusUrl).subscribe(
        (val: WrittenCTIRequiredV2Response) => {
          observer.next(val);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  //IDG WRITTEN
  public IDG_createWrittenCti(data) {
    return this.http
      .post(`${this.certificationUrl}/certifications`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart WRITTEN Create Endpoint v2
  public PatientChart_createWrittenCti(data) {
    return this.http
      .post(
        `${this.certificationUrl}/certifications/written/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart VERBAL Create Endpoint v2
  public PatientChart_createVerbalCti(data) {
    return this.http
      .post(
        `${this.certificationUrl}/certifications/verbals/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Referral VERBAL
  public Referral_createVerbalCti(data) {
    return this.http
      .post(`${this.certificationUrl}/certifications/verbals/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart WRITTEN Update Endpoint
  public PatientChart_UpdateWrittenCti(data) {
    return this.http
      .put(
        `${this.certificationUrl}/certifications/written/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart VERBAL Update Endpoint
  public PatientChart_UpdateVerbalCti(data) {
    return this.http
      .put(
        `${this.certificationUrl}/certifications/verbals/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Referral VERBAL Update Endpoint
  public Referral_UpdateVerbalCti(data) {
    return this.http
      .put(`${this.certificationUrl}/certifications/verbals/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //IDG VERBAL Update Endpoint
  public IDG_UpdateWrittenCti(data) {
    return this.http
      .put(`${this.certificationUrl}/certifications/written/idg`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  public createVerbalReferralCti(data) {
    // TODO: Put in real behavior
    return new Observable((observer) => {
      observer.next();
      observer.complete();
    });
  }

  // TODO: Refactor this lookup calls to return an observable that emits a defined object and not JSON output
  public getHospiceInfo(): Observable<any> {
    return this.http.get(`${this.certificationUrl}/hospice`).pipe(
      catchError((_err: HttpErrorResponse) => {
        // TODO: We shouldn't be returning some generic placeholder 'Error Hospice'
        return of({
          id: 697198,
          name: "Error hospice",
          phone: "2149112334",
          address: {
            street: "12 Main",
            mailing: "",
            city: "Norman",
            state: "OK",
            zipCode: "73071 1234",
          },
        });
        // return of(err);
      })
    );
  }

  public getCertificationPeriod(
    patientId: string
  ): Observable<CertificationPeriod> {
    const certBenefitUrl = `${this.certificationUrl}/certifications/${patientId}/benefit`;

    return new Observable((observer) => {
      this.http.get(certBenefitUrl).subscribe(
        (next: CertificationPeriod) => {
          observer.next(next);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  public getCertificationF2FNarrative(
    patientId: string
  ): Observable<CertificationF2FNarrative> {
    const certNarrativeUrl = `${this.certificationUrl}/certifications/${patientId}/face-to-face`;

    return new Observable((observer) => {
      this.http.get(certNarrativeUrl).subscribe(
        (next: CertificationF2FNarrative) => {
          observer.next(next);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getPhysicianData() {
    let url = `${this.idgDataUrl}/company-resources/persons/physicians/all`;
    return new Observable((observer) => {
      this.http
        .get(url, {
          headers: new HttpHeaders().set("Content-Type", "application/json"),
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getPhysicianDataCredential() {
    let url = `${this.resourceManagementUrl}/company-resources?page=0&limit=5000`;
    return new Observable((observer) => {
      this.http
        .get(url, {
          headers: new HttpHeaders().set("Content-Type", "application/json"),
        })
        .subscribe(
          (data) => {
            observer.next(data["content"]);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  getUsers(currentUserQbosId) {
    let url = `${this.resourceManagementUrl}/company-resource/persons/${currentUserQbosId}/credentials`;
    return new Observable((observer) => {
      this.http
        .get(url, {
          headers: new HttpHeaders().set("Content-Type", "application/json"),
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getPatientCertifications(patientId: string): Observable<any> {
    return this.http.get(
      `${this.certificationUrl}/certifications/${patientId}`
    );
  }

  public getCertificationList(patientId: string, url): Observable<any> {
    return this.http.get(
      `${this.certificationUrl}/certifications/${patientId}?${url}`
    );
  }

  public lookupPhysicians(searchText) {
    let url = `${
      this.config.getConfig().referralUrl
    }physicians/search?searchKey=${searchText}`;
    return new Observable((observer) => {
      this.http
        .get(url, {
          headers: new HttpHeaders().set("Content-Type", "application/json"),
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  // CTIv2 getAll endpoint
  public getCertificationListV2(patientId: string): Observable<any> {
    let params = new HttpParams().set("size", "999");
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.http.get<ContentWrapper<any>>(
      `${this.certificationUrl}/v2/certifications/${patientId}?type=VERBAL_PATIENT_CHART_V2&type=WRITTEN_PATIENT_CHART_V2&type=VERBAL_REFERRAL_V2&type=WRITTEN_REFERRAL_V2`,
      { headers: headers, params: params }
    );
  }

  // Retrieve Written CTIv2
  public getWrittenCertificationListV2(patientId: string) {
    return this.http.get(
      `${this.certificationUrl}/v2/certifications/${patientId}?type=WRITTEN_REFERRAL_V2&type=WRITTEN_PATIENT_CHART_V2`
    );
  }

  // Verbal CTIv2 referral post endpoint
  public Referral_CreateVerbalCTIv2(data) {
    return this.http
      .post(`${this.certificationUrl}/v2/certifications/verbals/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  // Written CTIv2 written post endpoint
  public Referral_CreateWrittenCTIv2(data) {
    return this.http
      .post(`${this.certificationUrl}/v2/certifications/written/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  // Verbal CTIv2 OnService post endpoint
  public PatientChart_CreateVerbalCTIv2(data) {
    return this.http
      .post(
        `${this.certificationUrl}/v2/certifications/verbals/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  // Written CTIv2 OnService post endpoint
  public PatientChart_CreateWrittenCTIv2(data) {
    return this.http
      .post(
        `${this.certificationUrl}/v2/certifications/written/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart WRITTEN Update Endpoint v2
  public PatientChart_UpdateWrittenCtiv2(data) {
    return this.http
      .put(
        `${this.certificationUrl}/v2/certifications/written/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  // Referral WRITTEN Update Endpoint v2
  public Referral_UpdateWrittenCtiv2(data) {
    return this.http
      .put(`${this.certificationUrl}/v2/certifications/written/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Patient Chart VERBAL Update Endpoint v2
  public PatientChart_UpdateVerbalCtiv2(data) {
    return this.http
      .put(
        `${this.certificationUrl}/v2/certifications/verbals/patient-chart`,
        data
      )
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  //Referral VERBAL Update Endpoint v2
  public Referral_UpdateVerbalCtiv2(data) {
    return this.http
      .put(`${this.certificationUrl}/v2/certifications/verbals/referral`, data)
      .catch((error) => {
        return Observable.throw(error.error);
      });
  }

  // get missing patient benefit periods
  public getMissingPatientBenefitPeriods(patientId, date: string) {
    const certBenefitUrl = `${this.certificationUrl}/v2/benefit-periods/${patientId}/missing?intervalValidDate=${date}`;
    return new Observable((observer) => {
      this.http.get(certBenefitUrl).subscribe(
        (next: MissingBenefitPeriod) => {
          observer.next(next);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  // Get current benefit period
  public GetCurrentBenefitPeriod(patientId, date: string) {
    const cacheKey = `${patientId}_${date}`;
    if (this.benefitPeriodCache.has(cacheKey)) {
      return this.benefitPeriodCache.get(cacheKey);
    }
    const shared$ = this.http
      .get(
        `${this.certificationUrl}/v2/benefit-periods/${patientId}/current?currentDate=${date}`
      )
      .pipe(
        finalize(() => this.benefitPeriodCache.delete(cacheKey)),
        shareReplay(1)
      );
    this.benefitPeriodCache.set(cacheKey, shared$);
    return shared$;
  }

  public getCurrentAndSoonBenefits(patientId, withinDays: number = 15) {
    let todayDate = moment().local(true).format("YYYY-MM-DD");
    return this.http.get(
      `${this.certificationUrl}/v2/benefit-periods/${patientId}/current-and-soon?currentDate=${todayDate}&withinDays=${withinDays}`
    );
  }

  public getPreviousCurrentAndSoonBenefits(patientId, withinDays: number = 15) {
    let todayDate = moment().local(true).format("YYYY-MM-DD");
    return this.http.get(
      `${this.certificationUrl}/v2/benefit-periods/${patientId}/previous-current-and-soon?currentDate=${todayDate}&withinDays=${withinDays}`
    );
  }

  // Get certificate history
  public GetCertificateHistory(certificateId) {
    return this.http.get(
      `${this.certificationUrl}/v2/certifications/history/${certificateId}`
    );
  }

  private getReportByV2CertificationType(
    type: CertificationV2TypeEnum
  ): CertificationReportType {
    switch (type) {
      case CertificationV2TypeEnum.WRITTEN_REFERRAL_V2:
      case CertificationV2TypeEnum.WRITTEN_PATIENT_CHART_V2:
        return CertificationReportType.PATIENT_CERTIFICATION;
      case CertificationV2TypeEnum.VERBAL_REFERRAL_V2:
      case CertificationV2TypeEnum.VERBAL_PATIENT_CHART_V2:
        return CertificationReportType.PATIENT_CERTIFICATION_VERBAL_REFERRAL;
    }
  }
  public printCertificateReport(
    certificationId: number,
    certificationType: CertificationV2TypeEnum
  ) {
    let reportType: CertificationReportType =
      this.getReportByV2CertificationType(certificationType);
    let url: string = `${this.reportsUrl}/reports/${reportType}`;
    let payload = {
      certificationId,
    };

    try {
      return this.http.post(url, payload, { responseType: "arraybuffer" });
    } catch (error) {
      console.error("Error during certificate printing. ", error);
    }
  }

  public getUpcomingBenefitPeriod(patientId, benefitPeriod) {
    return this.http.get(
      `${this.certificationUrl}/v2/benefit-periods/${patientId}/period/${benefitPeriod}/upcoming`
    );
  }
}



// WEBPACK FOOTER //
// ./src/certification/services/certification-service.ts